// Server-sent events over fetch (API-040 `streamInstallerEvents`). fetch rather than EventSource so
// the client controls reconnection: it resumes with `Last-Event-ID` = the last `message` seq it
// saw (only `message` events carry an id; state/change/budget never move the resume point, and
// every reconnect starts with a fresh snapshot), backs off between attempts, and stops on 401 or a
// non-retryable error instead of retrying forever.
import type { components } from "../api/schema";
import type { FetchLike } from "@nexus/ui";
import { ApiError, NetworkError, toApiError } from "@nexus/ui";

export interface SseEvent {
  event: string;
  data: string;
  /** The event's own `id:` field, or null when it carried none. */
  id: string | null;
}

/** An incremental parser for the WHATWG event-stream format (CR/LF/CRLF, comments, split chunks). */
export class SseParser {
  private buffer = "";
  private event = "";
  private data: string[] = [];
  private id: string | null = null;

  push(chunk: string): SseEvent[] {
    this.buffer += chunk;
    const events: SseEvent[] = [];
    let newline = this.buffer.search(/\r\n|\r|\n/);
    while (newline >= 0) {
      // A lone trailing CR may be the first half of a CRLF still in flight.
      if (this.buffer[newline] === "\r" && newline === this.buffer.length - 1) break;
      const width = this.buffer.startsWith("\r\n", newline) ? 2 : 1;
      const line = this.buffer.slice(0, newline);
      this.buffer = this.buffer.slice(newline + width);
      const completed = this.line(line);
      if (completed) events.push(completed);
      newline = this.buffer.search(/\r\n|\r|\n/);
    }
    return events;
  }

  private line(line: string): SseEvent | undefined {
    if (line === "") return this.dispatch();
    if (line.startsWith(":")) return undefined;
    const colon = line.indexOf(":");
    const field = colon < 0 ? line : line.slice(0, colon);
    let value = colon < 0 ? "" : line.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "event") this.event = value;
    else if (field === "data") this.data.push(value);
    else if (field === "id" && !value.includes("\u0000")) this.id = value;
    return undefined;
  }

  private dispatch(): SseEvent | undefined {
    const event = this.data.length > 0 ? { event: this.event || "message", data: this.data.join("\n"), id: this.id } : undefined;
    this.event = "";
    this.data = [];
    this.id = null;
    return event;
  }
}

/** Every complete event of a response body, as it arrives. An unterminated tail is dropped (WHATWG). */
export async function* readSse(body: ReadableStream<Uint8Array>): AsyncGenerator<SseEvent> {
  const parser = new SseParser();
  const decoder = new TextDecoder();
  const reader = body.getReader();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      yield* parser.push(decoder.decode(value, { stream: true }));
    }
    yield* parser.push(decoder.decode());
  } finally {
    reader.releaseLock();
  }
}

export type StreamStatus = "connecting" | "open" | "reconnecting" | "closed";

export interface StreamOptions {
  url: string;
  fetch: FetchLike;
  /** Resume point from an earlier view of the stream. */
  lastEventId?: string | null;
  onEvent(event: SseEvent): void;
  onStatus?(status: StreamStatus): void;
  /** A terminal error: the stream will not reconnect (401, 403, 404, a non-retryable 409 …). */
  onError?(error: ApiError): void;
  onSessionExpired?(): void;
  /** Back-off between reconnects; the last value repeats. */
  retryDelaysMs?: readonly number[];
  sleep?(ms: number, signal: AbortSignal): Promise<void>;
}

export interface EventStream {
  close(): void;
  lastEventId(): string | null;
  /** Settles once the stream has stopped for good. */
  readonly done: Promise<void>;
}

const DEFAULT_RETRY_DELAYS_MS = [1_000, 2_000, 5_000, 10_000, 30_000] as const;

function defaultSleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener("abort", () => {
      clearTimeout(timer);
      resolve();
    });
  });
}

async function errorBody(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}

export function openEventStream(options: StreamOptions): EventStream {
  const controller = new AbortController();
  const delays = options.retryDelaysMs ?? DEFAULT_RETRY_DELAYS_MS;
  const sleep = options.sleep ?? defaultSleep;
  let lastId = options.lastEventId ?? null;
  const status = (next: StreamStatus): void => options.onStatus?.(next);
  let attempt = 0;

  // One connection: "retry" when it should be re-opened, "stop" when it must not.
  async function connect(): Promise<"retry" | "stop"> {
    const headers: Record<string, string> = { Accept: "text/event-stream" };
    if (lastId !== null) headers["Last-Event-ID"] = lastId;
    let response: Response;
    try {
      response = await options.fetch(options.url, {
        method: "GET",
        headers,
        credentials: "same-origin",
        cache: "no-store",
        signal: controller.signal,
      });
    } catch {
      return "retry";
    }
    if (!response.ok || !response.body) {
      const error = response.ok ? new NetworkError() : toApiError(response.status, await errorBody(response));
      if (error.status === 401) options.onSessionExpired?.();
      if (error.retryable) return "retry";
      options.onError?.(error);
      return "stop";
    }
    status("open");
    attempt = 0;
    try {
      for await (const event of readSse(response.body)) {
        if (event.id !== null) lastId = event.id;
        options.onEvent(event);
      }
    } catch {
      // A dropped connection mid-stream: reconnect from the last id.
    }
    return "retry";
  }

  async function run(): Promise<void> {
    status("connecting");
    while (!controller.signal.aborted) {
      const outcome = await connect();
      if (outcome === "stop" || controller.signal.aborted) break;
      status("reconnecting");
      await sleep(delays[Math.min(attempt, delays.length - 1)] as number, controller.signal);
      attempt++;
    }
    status("closed");
  }

  const done = run();
  return {
    close: () => controller.abort(),
    lastEventId: () => lastId,
    done,
  };
}

export type InstallerStreamEvent = components["schemas"]["InstallerStreamEvent"];

// The SSE `event:` name → the member of the payload that must be present.
const INSTALLER_EVENT_MEMBERS: Readonly<Record<string, keyof InstallerStreamEvent>> = {
  message: "message",
  state: "session",
  change: "change",
  budget: "budget",
};

/** The typed payload of one installer stream event, or null when it is not one (ignored). */
export function parseInstallerEvent(event: SseEvent): InstallerStreamEvent | null {
  const member = INSTALLER_EVENT_MEMBERS[event.event];
  if (member === undefined) return null;
  let payload: unknown;
  try {
    payload = JSON.parse(event.data);
  } catch {
    return null;
  }
  if (typeof payload !== "object" || payload === null) return null;
  const typed = payload as InstallerStreamEvent;
  return typed.type === event.event && typed[member] !== undefined ? typed : null;
}
